import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import Slider from "react-slick";
import { toast } from "react-toastify";

// Icons
import {
  FiArrowLeft,
  FiArrowRight,
  FiArrowUpRight,
  FiCheck,
  FiCopy,
  FiGift,
  FiHeart,
  FiHome,
  FiMap,
  FiMapPin,
  FiNavigation,
  FiShare2,
  FiStar,
  FiTag,
  FiX,
} from "react-icons/fi";
import { FaWhatsapp, FaFacebookF, FaTwitter } from "react-icons/fa";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import Login1 from "../auth/Login1";
import SignUp1 from "../auth/SignUp1";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const BRAND      = "#700c28";
const BRAND_DARK = "#5a0a20";

const userLocation = JSON.parse(sessionStorage.getItem("userLocation"));

// Tag chips shown in each card's body
const TAGS = [
  { icon: FiHome,      label: "Residential" },
  { icon: FiStar,      label: "Featured"    },
  { icon: FiMap,       label: "Top Cities"  },
];

// Social share options for the share modal
const SHARE_LINKS = [
  {
    label: "WhatsApp",
    Icon: FaWhatsapp,
    bg: "bg-emerald-600 hover:bg-emerald-700",
    href: (url) => `https://wa.me/?text=${encodeURIComponent(url)}`,
  },
  {
    label: "Facebook",
    Icon: FaFacebookF,
    bg: "bg-blue-600 hover:bg-blue-700",
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    label: "Twitter",
    Icon: FaTwitter,
    bg: "bg-sky-500 hover:bg-sky-600",
    href: (url) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
  },
];

// Inner image carousel settings (fade between images)
const IMAGE_SLIDER_SETTINGS = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 800,
  fade: true,
  autoplay: true,
  autoplaySpeed: 3500,
  pauseOnHover: false,
  slidesToShow: 1,
  slidesToScroll: 1,
};

// ─────────────────────────────────────────────
// CSS injected once for animations
// ─────────────────────────────────────────────
const keyframes = `
/* Heart pop when toggled */
@keyframes ep-pop {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.35); }
  100% { transform: scale(1); }
}
/* Card entrance on mount */
@keyframes ep-fade-up {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: none; }
}
.ep-pop   { animation: ep-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1); }
.ep-enter { animation: ep-fade-up 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }

/* Outer slider track height fix */
.ep-slider .slick-list  { padding: 6px 0 20px; }
.ep-slider .slick-track { display: flex; }
.ep-slider .slick-slide { height: inherit; }
.ep-slider .slick-slide > div { height: 100%; }

/* Inner image carousel height fix */
.ep-img-slider,
.ep-img-slider .slick-list,
.ep-img-slider .slick-track,
.ep-img-slider .slick-slide,
.ep-img-slider .slick-slide > div { height: 100%; }
`;

// ─────────────────────────────────────────────
// Utility helpers
// ─────────────────────────────────────────────

/** Haversine formula — returns distance in km */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R    = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) *
    Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Returns a fully-qualified image URL or null */
function getValidImageUrl(img, BASE_URL) {
  if (typeof img !== "string" || img.trim() === "") return null;
  return img.startsWith("http") ? img : BASE_URL + img;
}

/** Maps the raw API category string to a display label */
function getCategoryLabel(raw) {
  const c = (raw || "").toLowerCase();
  if (c.includes("rent"))             return "FOR RENT";
  if (c.includes("commercial buy"))   return "COMMERCIAL BUY";
  if (c.includes("commercial lease")) return "COMMERCIAL LEASE";
  if (c.includes("pg"))               return "PG / CO-LIVING";
  return "FOR BUY";
}

// ─────────────────────────────────────────────
// PropertyCard
// ─────────────────────────────────────────────
function PropertyCard({
  property,
  index,
  BASE_URL,
  isFavorite,
  distance,
  activeIndex,
  onSlideChange,
  onHeart,
  onShare,
}) {
  const [pulseKey, setPulseKey] = useState(0);

  // Collect cover + extra images, filter out empties
  const allImages = [
    getValidImageUrl(property.cover_image, BASE_URL),
    ...(property.property_images || []).map((obj) =>
      getValidImageUrl(obj?.image, BASE_URL)
    ),
  ].filter(Boolean);

  // Progress bar width for multi-image cards
  const currentSlide = allImages.length ? (activeIndex || 0) % allImages.length : 0;
  const progress     = allImages.length > 1
    ? ((currentSlide + 1) / allImages.length) * 100
    : 100;

  const handleHeart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPulseKey((k) => k + 1); // re-trigger pop animation
    onHeart(property);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onShare(property._id);
  };

  return (
    <div
      className="h-full px-3 py-2 ep-enter"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <Link
        to={`/propertydetails/${property._id}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        style={{ textDecoration: "none" }}
        className="group h-full flex flex-col rounded-xl bg-white overflow-hidden
                   no-underline hover:no-underline
                   shadow-[0_5px_18px_rgba(17,17,17,0.07)]
                   hover:shadow-[0_12px_32px_rgba(112,12,40,0.15)]
                   hover:scale-[1.02]
                   transition-all duration-500 ease-in-out"
      >

        {/* ── IMAGE / CAROUSEL AREA ── */}
        <div className="relative h-44 bg-gray-100 overflow-hidden">

          {/* Multi-image fade carousel or single image */}
          {allImages.length > 1 ? (
            <Slider
              key={`${property._id}-${allImages.length}`}
              {...IMAGE_SLIDER_SETTINGS}
              initialSlide={activeIndex || 0}
              beforeChange={(_, next) => onSlideChange(property._id, next)}
              className="ep-img-slider"
            >
              {allImages.map((url, idx) => (
                <div key={`${property._id}-${idx}`} className="h-44">
                  <img
                    src={url}
                    alt="Property"
                    loading="lazy"
                    className="object-cover w-full h-44 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              ))}
            </Slider>
          ) : (
            <img
              src={allImages[0] || "/image/property-search.jpg"}
              alt="Property"
              loading="lazy"
              className="object-cover w-full h-44 transition-transform duration-700 ease-out group-hover:scale-105"
            />
          )}

          {/* Dark gradient at the bottom of the image */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* Top-left: Special Offer badge */}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-0.5 text-[10px] font-semibold text-[#700c28] shadow-md">
            <FiGift size={11} /> Special Offer
          </span>

          {/* Top-right: Favorite + Share buttons */}
          <div className="absolute top-2.5 right-2.5 flex flex-col gap-1.5">
            <button
              onClick={handleHeart}
              aria-label="Toggle favorite"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-md transition-all duration-300 hover:scale-110 hover:bg-white active:scale-95"
            >
              <span key={pulseKey} className={`inline-flex ${pulseKey ? "ep-pop" : ""}`}>
                <FiHeart
                  size={13}
                  style={{
                    color: isFavorite ? BRAND : "#4B5563",
                    fill:  isFavorite ? BRAND : "transparent",
                    transition: "fill 0.3s, color 0.3s",
                  }}
                />
              </span>
            </button>

            <button
              onClick={handleShare}
              aria-label="Share property"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-md text-gray-600 transition-all duration-300 hover:scale-110 hover:text-[#700c28] active:scale-95"
            >
              <FiShare2 size={12} />
            </button>
          </div>

          {/* Bottom-left: Category badge  |  Bottom-right: Distance chip */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
            <span className="inline-flex items-center gap-1 rounded bg-[#700c28] px-2 py-0.5 text-[8px] font-bold tracking-wider text-white shadow">
              <FiTag size={9} /> {getCategoryLabel(property.property_category_type)}
            </span>
            {distance && (
              <span className="inline-flex items-center gap-1 rounded bg-black/40 backdrop-blur px-2 py-0.5 text-[8px] font-semibold text-white">
                <FiNavigation size={9} /> {distance} km
              </span>
            )}
          </div>

          {/* Slide progress bar (only for multi-image) */}
          {allImages.length > 1 && (
            <div className="absolute bottom-0 left-0 h-0.5 w-full bg-white/25">
              <div
                className="h-full bg-white transition-all duration-700 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>

        {/* ── CONTENT AREA ── */}
        <div className="flex flex-col flex-grow p-4">

          {/* Property name */}
          <h4 className="text-base font-bold text-[#1a1a1a] truncate transition-colors duration-300 group-hover:text-[#700c28]">
            {property.property_name}
          </h4>

          {/* Address */}
          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 truncate">
            <FiMapPin size={12} className="shrink-0 text-[#700c28]" />
            <span className="truncate">{property.address_area || "Testing"}</span>
          </p>

          {/* Tag chips */}
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {TAGS.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center gap-0.5 rounded-lg
                           bg-[#700c28]/5 py-2
                           text-[9px] font-medium text-gray-600
                           group-hover:bg-[#700c28] group-hover:text-white
                           transition-all duration-300 cursor-default"
              >
                <Icon size={13} style={{ color: "currentColor" }} />
                {label}
              </div>
            ))}
          </div>

          {/* Explore button */}
          <div className="mt-auto pt-4">
            <div className="relative flex items-center justify-between overflow-hidden rounded-lg bg-[#700c28] px-3 py-2 text-xs font-semibold text-white">
              <span className="relative z-10">Explore Offer</span>
              <span className="relative z-10 flex h-5 w-5 items-center justify-center rounded-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <FiArrowUpRight size={11} />
              </span>
              <span
                className="absolute inset-y-0 left-0 w-0 transition-all duration-500 ease-in-out group-hover:w-full"
                style={{ background: BRAND_DARK }}
              />
            </div>
          </div>

        </div>
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────
// RecommendedProperties (main export)
// ─────────────────────────────────────────────
const RecommendedProperties = ({
  openRecommendedShareModal,
  closeShareModal,
  copyLink,
  currentShareUrl,
  data,
  fetchHomeData,
}) => {
  const [properties,        setProperties]       = useState([]);
  const [activeIndexes,     setActiveIndexes]    = useState({});
  const [isLoginModalOpen,  setIsLoginModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen]= useState(false);
  const [copied,            setCopied]           = useState(false);

  const sliderRef = useRef(null);
  const history   = useHistory();
  const userId    = sessionStorage.getItem("accessToken");
  const BASE_URL  = process.env.REACT_APP_API_URL;

  // ── Data loading ──────────────────────────
  useEffect(() => {
    if (data?.status === 1 && Array.isArray(data.data)) {
      setProperties(data.data);
    } else {
      setProperties([]);
    }
  }, [data]);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // ── Navigation ────────────────────────────
  const handleClick = () => history.push("/featuredDashboard");

  // ── Favorites ─────────────────────────────
  const addToFavorites = async (propertyId) => {
    if (!userId) {
      setIsLoginModalOpen(true);
      return;
    }
    try {
      await axios.post(`${BASE_URL}/cust_api/add_to_favorite`, {
        user_id: userId,
        property_id: propertyId,
      });
      fetchHomeData();
    } catch {
      toast.error("Failed to save the property. Please try again.");
      fetchHomeData();
    }
  };

  const removeFromFavorites = async (favoriteId) => {
    if (!userId) {
      setIsLoginModalOpen(true);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("favorite_id", favoriteId);
      await axios.delete(`${BASE_URL}/cust_api/remove_from_favorite`, {
        data: formData,
      });
      fetchHomeData();
    } catch {
      toast.error("Failed to remove the property. Please try again.");
      fetchHomeData();
    }
  };

  const handleFavoriteToggle = (property) => {
    if (!userId) { setIsLoginModalOpen(true); return; }
    property.is_favorite
      ? removeFromFavorites(property.favorite_id)
      : addToFavorites(property._id);
  };

  // ── Slide tracking ────────────────────────
  const handleSlideChange = (propertyId, next) =>
    setActiveIndexes((prev) => ({ ...prev, [propertyId]: next }));

  // ── Copy link ─────────────────────────────
  const handleCopy = (e) => {
    copyLink(e);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // ── Distance helper ───────────────────────
  const getDistance = (property) => {
    if (!userLocation || !property.latitude || !property.longitude) return null;
    return calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      parseFloat(property.latitude),
      parseFloat(property.longitude)
    ).toFixed(1);
  };

  // ── Outer slider settings ─────────────────
  const sliderSettings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: false,
    cssEase: "ease-in-out",
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 768,  settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  // ── Render ────────────────────────────────
  return (
    <>
      <style>{keyframes}</style>

      <section className="px-4 sm:px-10 pt-2 pb-6 bg-white font-sans overflow-hidden">
        <div className="mx-auto w-full max-w-[1400px]">

          {/* ── HEADER ── */}
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between px-2 sm:px-0">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#700c28]/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#700c28]">
                <FiStar size={10} /> Featured Properties
              </span>
              <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1a1a1a]">
                Exclusive{" "}
                <span className="bg-gradient-to-r from-[#700c28] to-[#b5173f] bg-clip-text text-transparent">
                  Featured Properties
                </span>
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">
                Handpicked homes that stand out. Go from browsing to buying
              </p>
            </div>

            <button
              onClick={handleClick}
              className="group inline-flex items-center gap-1.5 self-start sm:self-auto rounded-full border-2 border-[#700c28] px-4 py-2 text-xs font-semibold text-[#700c28] transition-all duration-300 hover:bg-[#700c28] hover:text-white hover:shadow-lg hover:shadow-[#700c28]/30 active:scale-95"
            >
              View All Properties
              <FiArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          {/* ── SLIDER WRAPPER ── */}
          <div className="relative mx-auto w-full px-6 sm:px-10 lg:px-6">

            {/* Prev arrow */}
            <button
              aria-label="Previous"
              onClick={() => sliderRef.current?.slickPrev()}
              className="group absolute left-0 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl bg-white text-[#700c28] shadow-xl ring-1 ring-black/5 transition-all duration-300 hover:scale-110 hover:bg-[#700c28] hover:text-white active:scale-90 lg:-left-2"
            >
              <FiArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
            </button>

            {/* Next arrow */}
            <button
              aria-label="Next"
              onClick={() => sliderRef.current?.slickNext()}
              className="group absolute right-0 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl bg-white text-[#700c28] shadow-xl ring-1 ring-black/5 transition-all duration-300 hover:scale-110 hover:bg-[#700c28] hover:text-white active:scale-90 lg:-right-2"
            >
              <FiArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>

            {/* Cards */}
            <Slider {...sliderSettings} className="ep-slider" ref={sliderRef}>
              {properties.map((property, index) => (
                <PropertyCard
                  key={property._id}
                  property={property}
                  index={index}
                  BASE_URL={BASE_URL}
                  isFavorite={!!property.is_favorite}
                  distance={getDistance(property)}
                  activeIndex={activeIndexes[property._id]}
                  onSlideChange={handleSlideChange}
                  onHeart={handleFavoriteToggle}
                  onShare={(id) => openRecommendedShareModal?.(id)}
                />
              ))}
            </Slider>
          </div>

        </div>
      </section>

      {/* ── SHARE MODAL ── */}
      <div
        id="shareModal"
        className="fixed inset-0 z-50 items-center justify-center hidden bg-black/60 backdrop-blur-sm p-4"
      >
        <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">

          {/* Title + close */}
          <div className="mb-4 flex items-center justify-between">
            <h5 className="text-base font-bold text-[#1a1a1a]">Share this property</h5>
            <button
              onClick={closeShareModal}
              aria-label="Close share modal"
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-all duration-300 hover:rotate-90 hover:bg-gray-100 hover:text-gray-700"
            >
              <FiX size={15} />
            </button>
          </div>

          {/* URL + copy */}
          <div className="mb-5 flex items-center gap-2">
            <input
              type="text"
              className="flex-grow rounded-lg border border-gray-200 p-2 text-xs focus:border-[#700c28] focus:outline-none"
              value={currentShareUrl}
              readOnly
            />
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-300 active:scale-95 ${
                copied ? "bg-emerald-600" : "bg-[#700c28] hover:bg-[#5a0a20]"
              }`}
            >
              {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          {/* Social share buttons */}
          <div className="grid grid-cols-3 gap-2">
            {SHARE_LINKS.map(({ label, Icon, bg, href }) => (
              <a
                key={label}
                href={href(currentShareUrl)}
                target="_blank"
                rel="noreferrer"
                className={`flex flex-col items-center gap-1 rounded-lg py-2 text-[10px] font-semibold text-white no-underline transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${bg}`}
              >
                <Icon size={14} />
                {label}
              </a>
            ))}
          </div>

        </div>
      </div>

      {/* ── AUTH MODALS ── */}
      <Login1
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSwitchToSignUp={() => {
          setIsLoginModalOpen(false);
          setIsSignUpModalOpen(true);
        }}
      />
      <SignUp1
        isOpen={isSignUpModalOpen}
        onClose={() => setIsSignUpModalOpen(false)}
        onSwitchToLogin={() => {
          setIsSignUpModalOpen(false);
          setIsLoginModalOpen(true);
          fetchHomeData();
        }}
      />
    </>
  );
};

export default RecommendedProperties;